// src/ClinicalDataCollector/components/output/HospitalDischargeFollowUpSummary.jsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useSelector } from 'react-redux';

import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const HospitalDischargeFollowUpSummary = () => {
  // 🔹 id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD (Hospital Discharge FU)
  const {
    hospitalDischargeNotes,
    hospitalDischargeLoading,
    hospitalDischargeError,
    loadHospitalDischargeNotes,
  } = useContext(AdvancedMdContext);

  // 🔹 mapa de notas abiertas/cerradas
  const [openMap, setOpenMap] = useState({});

  const toggleNote = noteId => {
    setOpenMap(prev => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  // 🔹 Cargar notas cuando tengamos advancedMdPatientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadHospitalDischargeNotes(advancedMdPatientId);
  }, [advancedMdPatientId, loadHospitalDischargeNotes]);

  const notes = hospitalDischargeNotes || [];
  const totalNotes = notes.length;

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <LocalHospitalIcon color="error" />
        <Box ml={1}>Clinical Notes – Hospital Discharge Follow Up</Box>
      </Typography>

      {!hospitalDischargeLoading &&
        !hospitalDischargeError &&
        totalNotes > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {totalNotes === 1
              ? '1 hospital discharge note'
              : `${totalNotes} hospital discharge notes`}
          </Typography>
        )}

      {hospitalDischargeLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading Hospital Discharge Follow Up notes from AdvancedMD…
        </Typography>
      )}

      {hospitalDischargeError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading Hospital Discharge Follow Up notes:{' '}
          {hospitalDischargeError.message || 'Unknown error'}
        </Alert>
      )}

      {!hospitalDischargeLoading &&
        notes.length === 0 &&
        !hospitalDischargeError && (
          <Typography variant="body2" color="text.secondary">
            No Hospital Discharge Follow Up notes available for this patient.
          </Typography>
        )}

      {notes.map(note => {
        const f = note.fields || {};
        const dateLabel = note.noteDateTime
          ? new Date(note.noteDateTime).toLocaleString()
          : 'N/A';

        const bpCurrent =
          f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
            ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
            : '';

        const bpPrev =
          f.Vitals_BP_SystolicPrev && f.Vitals_BP_DiastolicPrev
            ? `${f.Vitals_BP_SystolicPrev}/${f.Vitals_BP_DiastolicPrev}`
            : '';

        // 🔹 mini resumen: vitals + dx principal + lugar
        const vitalsSummary = [
          f.Vitals_Weight ? `Wt: ${f.Vitals_Weight}` : null,
          bpCurrent ? `BP: ${bpCurrent}` : null,
        ]
          .filter(Boolean)
          .join(' • ');

        const dxSummary = f.PMH2 || f.PMH || '';
        const locationSummary = f.location || '';

        const headerLine = [
          vitalsSummary,
          dxSummary ? `Dx: ${dxSummary}` : null,
          locationSummary ? `Location: ${locationSummary}` : null,
        ]
          .filter(Boolean)
          .join(' | ');

        const isOpen = !!openMap[note.noteId];

        return (
          <Box
            key={note.noteId}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header compacto de la nota */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {note.templateName} – {dateLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Entered by: {note.userName} | Signed by: {note.signedByUser}
              {note.reviewedByUser
                ? ` | Reviewed by: ${note.reviewedByUser}`
                : ''}
            </Typography>

            {headerLine && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {headerLine}
              </Typography>
            )}

            {/* Botón ver/ocultar detalle */}
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => toggleNote(note.noteId)}
              >
                {isOpen ? 'Hide details' : 'View details'}
              </Button>
            </Box>

            {/* Detalle completo dentro de Collapse */}
            <Collapse in={isOpen}>
              {/* Ubicación / centro */}
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Location: </strong>
                  {f.location || '—'}
                </Typography>
                {(f.location2 || f.location1) && (
                  <Typography variant="body2">
                    <strong>Address / Contact: </strong>
                    {[f.location2, f.location1]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2}>
                {/* Vitals */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vitals at Follow Up
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Weight</TableCell>
                        <TableCell>{f.Vitals_Weight || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Height</TableCell>
                        <TableCell>{f.Vitals_Height || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BMI</TableCell>
                        <TableCell>{f.Vitals_BMI || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Temp</TableCell>
                        <TableCell>
                          {f.Vitals_Temperature || '—'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Pulse</TableCell>
                        <TableCell>{f.Vitals_Pulse || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>O₂ Sat RA</TableCell>
                        <TableCell>{f.Vitals_O2SatRA || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BP (current)</TableCell>
                        <TableCell>{bpCurrent || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>BP (previous)</TableCell>
                        <TableCell>{bpPrev || '—'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>

                {/* Motivo + HPI / transición */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Reason for Appointment
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.ReasonforAppointment || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    HPI / Hospital Course
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {f.HistoryofPresentIllness || '—'}
                  </Typography>

                  {f.HistoryofPresentIllness1 && (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-line',
                        mt: 1,
                        fontSize: 12,
                      }}
                    >
                      {f.HistoryofPresentIllness1}
                    </Typography>
                  )}
                </Grid>

                {/* Diagnóstico, meds y plan */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Key Diagnoses at Discharge
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.PMH2 || f.PMH || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Current Medications
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.CurrentMedications || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Treatment Plan / Follow Up
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 1 }}
                  >
                    {f.Treatment || '—'}
                  </Typography>
                  {f.FollowUp && (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-line',
                        mt: 1,
                        fontStyle: 'italic',
                      }}
                    >
                      Follow-up: {f.FollowUp}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Opcional: Assessment textual */}
              {f.Assessments && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assessments
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {f.Assessments}
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default HospitalDischargeFollowUpSummary;
